import {
  CircleIconProps,
  InputFieldInputProps,
} from "../components/InputField/InputField.types";
import { CheckboxContainerProps } from "../components/Checkbox/Checkbox.types";
import { RadioButtonContainerProps } from "../components/RadioButton/RadioButton.types";
import { TopNavProps } from "../components/TopNav/TopNav.types";
import { AccordionContainerProps } from "../components/Accordion/Accordion.types";
import { colorGuide, colorPalette } from "../theme/colors";
import { SocialButtonProps } from "../components/SocialButton/SocialButton.types";

import { ActionButtonWrapperProps } from "../components/ActionButton/ActionButton.types";
import { ButtonWrapperProps } from "../components/Button/Button.types";
import { generateOTPFieldStylesProps } from "../components/OTPInputField/OTPInputField.types";
import { CountryCodeWrapperProps } from "../components/CountryCodeInput/CountryCodeInput.types";
import { MarginAndPaddingSpacingProps } from "../components/LegacyFlex/Flex.types";
import { PillStylesProps } from "../components/Pill/Pill.types";
import { ToastAction, ToastType } from "../components/Toast/Toast.types";
import { TagType } from "../components/Tags/Tags.types";
import { css } from "@emotion/react";

export const hexToRGBA = (hex: string, alpha: string | number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  if (alpha) return `rgba(${r},${g},${b}, ${alpha})`;
  return `rgb(${r},${g},${b})`;
};

function isObject(item: any): boolean {
  return item instanceof Object && !Array.isArray(item);
}

export function mergeDeep(target: any, ...sources: any[]): any {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return mergeDeep(target, ...sources);
}

export const generateTextStyles = ({
  type = "body",
  fontWeight = "semiBold",
  color = "inherit",
  variant = 1,
  fontFamily = "Rubik",
  textAlign = "left",
}) => {
  let letterSpacing = 0.2;

  if (type === "caps") {
    letterSpacing = 2;
  }

  const fontWeightMap: { [key: string]: number } = {
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
  };

  const fontSizeMap: {
    [key: string]: { [key: number]: { size: number; lineHeight: number } };
  } = {
    body: {
      6: {
        size: 11,
        lineHeight: 17,
      },
      5: {
        size: 12,
        lineHeight: 18,
      },
      4: {
        size: 13,
        lineHeight: 20,
      },
      3: {
        size: 14,
        lineHeight: 21,
      },
      2: {
        size: 15,
        lineHeight: 23,
      },
      1: {
        size: 16,
        lineHeight: 24,
      },
    },
    caps: {
      1: {
        size: 12,
        lineHeight: 15,
      },
      2: {
        size: 10,
        lineHeight: 13,
      },
    },
    heading: {
      7: {
        size: 13,
        lineHeight: 16,
      },
      6: {
        size: 14,
        lineHeight: 18,
      },
      5: {
        size: 16,
        lineHeight: 20,
      },
      4: {
        size: 18,
        lineHeight: 23,
      },
      3: {
        size: 20,
        lineHeight: 25,
      },
      2: {
        size: 22,
        lineHeight: 28,
      },
      1: {
        size: 44,
        lineHeight: 55,
      },
    },
    "serif-heading": {
      4: {
        size: 18,
        lineHeight: 23,
      },
      3: {
        size: 20,
        lineHeight: 25,
      },
      2: {
        size: 22,
        lineHeight: 28,
      },
      1: {
        size: 24,
        lineHeight: 30,
      },
    },
  };
  const { size, lineHeight } = fontSizeMap[type][variant];

  let fontWeightNumber = fontWeightMap[fontWeight];

  return css`
    letter-spacing: ${letterSpacing}px;
    font-style: normal;
    font-weight: ${fontWeightNumber};
    font-size: ${size}px;
    line-height: ${lineHeight}px;
    color: ${color};
    font-family: ${fontFamily}, serif;
    text-align: ${textAlign};
    ${type === "caps"
      ? css`
          text-transform: uppercase;
        `
      : ""}
  `;
};

export const generateButtonStyles = ({ variant }: ButtonWrapperProps) => {
  switch (variant) {
    case "primary":
      const primary = colorGuide.lightComponents.buttons.primary;
      return css`
        background-color: ${primary.backgroundColor};
        color: ${primary.color};
        &:disabled {
          background-color: ${primary.disabledColors
            .backgroundColor} !important;
          color: ${primary.disabledColors.color} !important;
        }
      `;
    case "secondary":
      const secondary = colorGuide.lightComponents.buttons.secondary;
      return css`
        background-color: ${secondary.backgroundColor};
        color: ${secondary.color};
        border: 1px solid ${secondary.borderColor};
        &:disabled {
          background-color: ${secondary.disabledColors.backgroundColor};
          color: ${secondary.disabledColors.color};
          border: 1px solid ${secondary.disabledColors.borderColor};
        }
      `;
    case "danger":
      const danger = colorGuide.lightComponents.buttons.danger;
      return css`
        background-color: ${danger.backgroundColor};
        color: ${danger.color};
        &:disabled {
          background-color: ${danger.disabledColors.backgroundColor};
          color: ${danger.disabledColors.color};
          border: 1px solid ${danger.disabledColors.borderColor};
        }
      `;
  }
};

export const generateSocialButtonStyles = ({ variant }: SocialButtonProps) => {
  const styles = {
    instagram: css`
      background: linear-gradient(90deg, #e03023 0%, #aa29ac 100%);
      border: 1px solid #fff;
    `,
    youtube: css`
      background: #f00;
      border: none;
    `,
    facebook: css`
      background: #176eff;
      border: none;
    `,
    telegram: css`
      background: #4a99d1;
      border: none;
    `,
    default: css`
      background: #ffffff;
      border: none;
    `,
  };

  return styles[variant] || styles.default;
};

export const generateAnnouncementCardStyles = ({
  state,
  isPinned,
}: {
  state: string;
  isPinned?: boolean;
}) => {
  if (isPinned) {
    return css`
      border: 1px solid ${colorPalette.info[500]};
      background: ${colorPalette.info[100]};
    `;
  } else if (state === "unread") {
    return css`
      border: 1px solid ${colorPalette.popWhite[100]};
      background: ${colorPalette.popWhite[500]};
    `;
  } else if (state === "read") {
    return css`
      border: 1px solid ${colorPalette.popWhite[300]};
      background: ${colorPalette.popWhite[300]};
    `;
  }

  return css``;
};

export const generateActionButtonStyles = ({
  variant,
  outlined,
}: ActionButtonWrapperProps) => {
  if (outlined && (variant === "primary" || variant === "danger")) {
    const palette =
      variant === "primary"
        ? colorGuide.lightComponents.actionButton.primary
        : colorGuide.lightComponents.actionButton.danger;
    return css`
      background-color: transparent;
      color: ${palette.outlineColor};
      border: 1px solid ${palette.outlineColor};
      &:disabled {
        color: ${palette.disabledColors.outlineColor} !important;
        border: 1px solid ${palette.disabledColors.outlineColor} !important;
      }
    `;
  }
  switch (variant) {
    case "primary":
      const primary = colorGuide.lightComponents.actionButton.primary;
      return css`
        background-color: ${primary.backgroundColor};
        color: ${primary.color};
        &:disabled {
          background-color: ${primary.disabledColors
            .backgroundColor} !important;
          color: ${primary.disabledColors.color} !important;
        }
      `;
    case "secondary":
      const secondary = colorGuide.lightComponents.actionButton.secondary;
      return css`
        background-color: ${secondary.backgroundColor};
        color: ${secondary.color};
        &:disabled {
          background-color: ${secondary.disabledColors.backgroundColor};
          color: ${secondary.disabledColors.color};
        }
      `;
    case "danger":
      const danger = colorGuide.lightComponents.actionButton.danger;
      return css`
        background-color: ${danger.backgroundColor};
        color: ${danger.color};
        &:disabled {
          background-color: ${danger.disabledColors.backgroundColor};
          color: ${danger.disabledColors.color};
        }
      `;
    case "quantity":
      return css`
        background-color: ${colorPalette.popOrange[300]};
        color: ${colorPalette.popWhite[500]};
        &:disabled {
          background-color: ${colorPalette.popOrange[200]};
          color: ${colorPalette.popWhite[300]};
          cursor: not-allowed;
        }
      `;
  }
};

export const generateCheckBoxStyles = ({
  checked,
  disabled,
}: CheckboxContainerProps) => {
  let backgroundColor = "transparent";
  let borderColor = "transparent";
  if (disabled && checked) {
    backgroundColor = colorGuide.lightComponents.checkbox.disabled.background;
    borderColor = colorGuide.lightComponents.checkbox.disabled.background;
  }
  if (disabled && !checked) {
    borderColor = colorGuide.lightComponents.checkbox.disabled.border;
  }
  if (!disabled && checked) {
    backgroundColor = colorGuide.lightComponents.checkbox.background;
  }
  if (!disabled && !checked) {
    borderColor = colorGuide.lightComponents.checkbox.border;
  }

  return css`
    background-color: ${backgroundColor};
    border: 1px solid ${borderColor};
  `;
};

export const generateRadioButtonStyles = ({
  selected,
  disabled,
}: RadioButtonContainerProps) => {
  let backgroundColor = "transparent";
  let borderColor = "transparent";

  if (disabled && selected) {
    backgroundColor =
      colorGuide.lightComponents.radioButton.disabled.selected.background;
    borderColor =
      colorGuide.lightComponents.radioButton.disabled.selected.border;
  } else if (disabled && !selected) {
    borderColor =
      colorGuide.lightComponents.radioButton.disabled.unselected.border;
  } else if (!disabled && selected) {
    backgroundColor =
      colorGuide.lightComponents.radioButton.selected.background;
    borderColor = colorGuide.lightComponents.radioButton.selected.border;
  } else if (!disabled && !selected) {
    borderColor = colorGuide.lightComponents.radioButton.unselected.border;
  }

  return {
    backgroundColor,
    borderColor,
  };
};

export const generateTopNavStyles = ({
  variant,
  showBackArrow,
  subtitle,
  isSupportIconVisible,
  isWebview,
}: TopNavProps & {
  showBackArrow?: boolean;
  isSupportIconVisible?: boolean;
  isWebview?: boolean;
}) => {
  const topPad = isWebview ? "0px" : "24px";
  switch (variant) {
    case "logo":
      return css`
        padding: 8px 20px;
        flex-direction: column;
        justify-content: flex-end;
        align-items: flex-start;
        padding-top: calc(8px + ${topPad});
      `;
    case "back":
      return css`
        padding: 18px 20px 20px 20px;
        justify-content: ${isSupportIconVisible
          ? "space-between"
          : "flex-start"};
        align-items: ${isSupportIconVisible ? "flex-start" : "center"};
        padding-top: calc(18px + ${topPad});
      `;
    case "close":
      return css`
        padding: 20px;
        justify-content: flex-end;
        align-items: center;
        padding-top: calc(20px + ${topPad});
      `;
    case "title-subtitle":
      return css`
        padding: ${subtitle ? "0px 20px 4px 20px" : "14px 20px 0px 20px"};
        align-items: flex-start;
        flex-direction: column;
        gap: ${showBackArrow ? "10px" : "4px"};
        padding-top: calc(${subtitle ? "0px" : "14px"} + ${topPad});
        ${!showBackArrow &&
        !isSupportIconVisible &&
        "justify-content: flex-end;"}
      `;
    default:
      return css``;
  }
};
export const generateAccordionContainerStyles = ({
  isOpen,
  disabled,
  backgroundColor,
}: AccordionContainerProps) => {
  return css`
    width: 100%;
    display: flex;
    padding: 8px 20px 0px 20px;
    box-sizing: border-box;
    flex-direction: column;
    align-items: flex-start;
    background: ${backgroundColor
      ? backgroundColor
      : disabled
      ? colorGuide.lightComponents.accordion.disabledColors.backgroundColor
      : colorGuide.lightComponents.accordion.background};
    transition: all 0.3s ease;
  `;
};

export const generateStepStyles = ({ isFilled }: { isFilled: boolean }) => {
  return css`
    height: 8px;
    flex: 1;
    border-radius: 4px;
    background-color: ${isFilled
      ? colorGuide.lightComponents.progressBar.backgroundColor
      : "transparent"};
    border: ${isFilled
      ? "none"
      : `2px solid ${colorGuide.lightComponents.progressBar.backgroundColor}`};
  `;
};

export const generateOTPInputFieldWrapperStyles = ({
  variant,
}: generateOTPFieldStylesProps) => {
  const colors = colorGuide.lightComponents.OTPInputField[variant];
  return css`
    border: 2px solid ${colors.border};
    background-color: ${colors.backgroundColor};
  `;
};

export const generateOTPFieldBoxStylesProps = ({
  variant,
}: generateOTPFieldStylesProps) => {
  const colors = colorGuide.lightComponents.OTPInputField[variant];
  return css`
    border-bottom: 2px solid ${colors.inputBorder};
  `;
};

export const generateInputFieldStyles = ({ variant }: InputFieldInputProps) => {
  switch (variant) {
    case "default":
      return css`
        background-color: transparent;
        border: 2px solid ${colorPalette.popWhite[300]};
      `;
    case "success":
      return css`
        background-color: ${colorPalette.success[100]};
        border: 2px solid ${colorPalette.success[300]};
      `;
    case "error":
      return css`
        background-color: ${colorPalette.error[100]};
        border: 2px solid ${colorPalette.error[300]};
      `;
  }
};

export const getCircleIconStyles = ({ variant }: CircleIconProps) => {
  switch (variant) {
    case "default":
      return css`
        color: ${colorPalette.black[200]};
      `;
    case "success":
      return css`
        color: ${colorPalette.black[200]};
      `;
    case "error":
      return css`
        color: ${colorPalette.error[500]};
      `;
  }
};

export const getCountryCodeStyles = ({ variant }: CountryCodeWrapperProps) => {
  switch (variant) {
    case "default":
      return css`
        background-color: ${colorPalette.popWhite[500]};
        border: 2px solid ${colorPalette.popWhite[300]};
      `;
    case "success":
      return css`
        background-color: ${colorPalette.success[100]};
        border: 2px solid ${colorPalette.success[300]};
      `;
  }
};

export const getMarginFromProps = (
  props: MarginAndPaddingSpacingProps
): string => {
  const { m = 0, mt = m, mb = m, ml = m, mr = m } = props;
  return `${mt}px ${mr}px ${mb}px ${ml}px`;
};
export const getPaddingFromProps = (
  props: MarginAndPaddingSpacingProps,
  type: "GRID" | "DIV"
): string => {
  let { p = 0, pt = p, pb = p, pl = p, pr = p } = props;
  if (type === "GRID" && props.gutter) {
    if (pl === 0) {
      pl = 20;
    }
    if (pr === 0) {
      pr = 20;
    }
  } else if (type === "DIV" && props.gutter) {
    if (pt === 0) {
      pt = 8;
    }
    if (pb === 0) {
      pb = 8;
    }
  }

  return `${pt}px ${pr}px ${pb}px ${pl}px`;
};

export const generatePillStyles = ({ selected, variant }: PillStylesProps) => {
  const colorVariants = {
    default: {
      selected: {
        background:
          colorGuide.lightComponents.pills.default.selected.background,
        border: colorGuide.lightComponents.pills.default.selected.border,
      },
      unSelected: {
        background:
          colorGuide.lightComponents.pills.default.unSelected.background,
        border: colorGuide.lightComponents.pills.default.unSelected.border,
      },
    },
    primary: {
      selected: {
        background:
          colorGuide.lightComponents.pills.primary.selected.background,
        border: colorGuide.lightComponents.pills.primary.selected.border,
      },
      unSelected: {
        background:
          colorGuide.lightComponents.pills.primary.unSelected.background,
        border: colorGuide.lightComponents.pills.primary.unSelected.border,
      },
    },
    tag: {
      selected: {
        background: colorGuide.lightComponents.pills.tag.selected.background,
        border: colorGuide.lightComponents.pills.tag.selected.border,
      },
      unSelected: {
        background: colorGuide.lightComponents.pills.tag.unSelected.background,
        border: colorGuide.lightComponents.pills.tag.unSelected.border,
      },
    },
    label: {
      selected: {
        background: colorGuide.lightComponents.pills.label.selected.background,
        border: colorGuide.lightComponents.pills.label.selected.border,
      },
      unSelected: {
        background:
          colorGuide.lightComponents.pills.label.unSelected.background,
        border: colorGuide.lightComponents.pills.label.unSelected.border,
      },
    },
  };

  const selectedVariant =
    variant && colorVariants[variant] ? variant : "default";
  const colors = colorVariants[selectedVariant];

  return `
    background-color: ${
      selected ? colors.selected.background : colors.unSelected.background
    };
    border: 1px solid ${
      selected ? colors.selected.border : colors.unSelected.border
    };
  `;
};

export const generateToastStyles = ({
  action,
  type,
}: {
  action: ToastAction;
  type: ToastType;
}) => {
  // Define background colors based on type
  const backgroundColors = {
    neutral: colorGuide.lightComponents.toastMessage.neutral.backgroundColor,
    negative: colorGuide.lightComponents.toastMessage.negative.backgroundColor,
    positive: colorGuide.lightComponents.toastMessage.positive.backgroundColor,
  };

  // Define text color
  const textColor = {
    neutral: colorGuide.lightComponents.toastMessage.neutral.textColor,
    negative: colorGuide.lightComponents.toastMessage.negative.textColor,
    positive: colorGuide.lightComponents.toastMessage.positive.textColor,
  };

  // Define layout based on action type
  const layoutStyles = {
    dismissable: {
      display: "inline-flex",
      gap: "4px",
    },

    link: {
      display: "flex",
      justifyContent: "space-between",
    },

    icon: {
      display: "inline-flex",
      justifyContent: "center",
      gap: "10px",
    },

    none: {
      display: "inline-flex",
      justifyContent: "center",
      gap: "10px",
      borderRadius: "20px",
    },
  };

  return {
    ...(layoutStyles[action] || layoutStyles["none"]),
    backgroundColor: backgroundColors[type],
    color: textColor[type],
  };
};

export const generateTagStyles = ({ type }: { type: TagType }) => {
  switch (type) {
    case "non-partner":
      return css`
        background: ${colorPalette.popWhite[100]};
        color: ${colorPalette.popBlack[500]};
        padding: 2px 4px;
      `;
    case "brand":
      return css`
        background: ${colorPalette.warning[500]};
        color: ${colorPalette.popBlack[500]};
        padding: 2px 4px;
      `;
    case "blacklisted":
      return css`
        background: ${colorPalette.error[500]};
        color: ${colorPalette.popWhite[500]};
        padding: 2px 4px 2px 0px;
      `;
    case "upto":
      return css`
        background: ${colorPalette.info[500]};
        color: ${colorPalette.popWhite[500]};
        padding: 2px;
      `;
    case "flat":
      return css`
        background: ${colorPalette.popOrange[500]};
        color: ${colorPalette.popWhite[500]};
        padding: 2px;
      `;
    case "range":
      return css`
        background: #ff426f;
        color: ${colorPalette.popWhite[500]};
        padding: 2px;
      `;
    case "payout":
      return css`
        background: #ff426f;
        color: ${colorPalette.popWhite[500]};
        padding: 1px 4px 1px 2px;
        border-radius: 17px;
        gap: 2px;
      `;
    case "category":
      return css`
        background: ${colorPalette.mannna[400]};
        color: ${colorPalette.popBlack[500]};
        padding: 3px 8px 3px 8px;
        border-radius: 17px;
      `;
    case "unselected":
      return css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        color: ${colorPalette.popBlack[200]};
        border: 1px solid ${colorPalette.white[100]};
        padding: 2px 4px;
      `;
    case "round":
      return css`
        background: #ff426f;
        color: ${colorPalette.popWhite[500]};
        display: inline-flex;
        padding: 1px 4px;
        gap: 2px;
        border-radius: 17px;
      `;
    case "whitelist":
      return css`
        background: ${colorPalette.error[500]};
        color: ${colorPalette.popBlack[500]};
        padding: 1px 4px 1px 2px;
        justify-content: center;
        align-items: center;
        border-radius: 16px;
      `;
    case "track":
      return css`
        background: ${colorPalette.error[500]};
        color: ${colorPalette.popWhite[500]};
        padding: 2px 4px 2px 0px;
        justify-content: center;
        align-items: center;
        border-radius: 2px;
        gap: 4px;
        border: 1px solid ${colorPalette.error[500]};
      `;
    default:
      return css`
        background: #e0e0e0;
        color: #000;
      `;
  }
};

export const getFileUploadStatusColor = (
  status:
    | "uploaded"
    | "failed"
    | "uploading"
    | "DRAFT_PENDING"
    | "DRAFT_APPROVED"
    | "DRAFT_REJECTED"
    | "default"
) => {
  switch (status) {
    case "uploaded":
      return colorPalette.success[300];
    case "failed":
      return colorPalette.error[400];
    default:
      return colorPalette.popBlack[100];
  }
};
